import React, { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import DndLoader from "@/components/DndLoader";

// Context
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// API Configuration - fallback to relative URL if env not set
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Log API URL in development
if (process.env.NODE_ENV === 'development') {
  console.log('API URL:', API);
}

// Add token to all requests if available
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global response interceptor:
//  - 401 / 403 with "token" → single source of truth for logout (clear session + cached_user, bounce to /)
//  - Network errors are just logged; UI keeps cached state so transient mobile tab-resume blips don't kick users out.
let _isHandlingAuthFailure = false;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.code === 'ERR_NETWORK') {
      console.warn('[axios] Network error:', error.config?.url);
      return Promise.reject(error);
    }
    const status = error?.response?.status;
    if (status === 401 && !_isHandlingAuthFailure) {
      _isHandlingAuthFailure = true;
      try {
        localStorage.removeItem('session_token');
        localStorage.removeItem('cached_user');
      } catch (e) { console.warn('[axios] clear storage failed', e); }
      // Defer so any pending React updates can settle
      setTimeout(() => {
        _isHandlingAuthFailure = false;
        if (window.location.pathname !== '/' && !window.location.pathname.startsWith('/auth/')) {
          window.location.replace('/');
        }
      }, 50);
    }
    return Promise.reject(error);
  }
);

// ==================== AUTH CALLBACK (New Google OAuth) ====================
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Get token from URL query params (new Google OAuth flow)
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const isNewAccount = params.get('new_account') === 'true';
    const error = params.get('error');

    if (error) {
      console.error('Auth error:', error);
      toast.error(`Authentication failed: ${error}`);
      navigate('/', { replace: true });
      return;
    }

    if (token) {
      // Store token
      localStorage.setItem('session_token', token);
      
      // Fetch user data
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(response => {
          const userData = response.data;
          
          if (isNewAccount) {
            toast.success('Account created successfully! Welcome to BeyondTheTable!');
          } else {
            toast.success(`Welcome back, ${userData.name}!`);
          }
          
          // Clean URL and navigate
          window.history.replaceState(null, '', '/hub');
          navigate('/hub', { state: { user: userData }, replace: true });
        })
        .catch(error => {
          console.error('Auth error:', error);
          localStorage.removeItem('session_token');
          toast.error('Authentication failed');
          navigate('/', { replace: true });
        });
    } else {
      navigate('/', { replace: true });
    }
  }, [navigate, location.search]);

  return <DndLoader label="Authenticating..." />;
};

// ==================== PROTECTED ROUTE ====================
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      try { localStorage.setItem('cached_user', JSON.stringify(location.state.user)); }
      catch (e) { console.warn('[ProtectedRoute] cache user failed', e); }
      return;
    }

    const token = localStorage.getItem('session_token');
    if (!token) {
      setIsAuthenticated(false);
      navigate('/', { replace: true });
      return;
    }

    // OPTIMISTIC HYDRATE: render immediately from cached_user so tab-resume / reload don't flash a login screen.
    // The global axios 401 interceptor (in App.js) is the single source of truth for logging users out.
    const cachedRaw = localStorage.getItem('cached_user');
    if (cachedRaw) {
      try {
        const cachedUser = JSON.parse(cachedRaw);
        setUser(cachedUser);
        setIsAuthenticated(true);
      } catch (e) { console.warn('[ProtectedRoute] parse cache failed', e); }
    }

    // Background verify: refresh cached user. 401s are handled globally; network errors keep cached state.
    axios.get(`${API}/auth/me`)
      .then(response => {
        setUser(response.data);
        setIsAuthenticated(true);
        try { localStorage.setItem('cached_user', JSON.stringify(response.data)); }
        catch (e) { console.warn('[ProtectedRoute] cache user failed', e); }
      })
      .catch(error => {
        // 401 is handled by the global interceptor.
        // If we have NO cached user AND this isn't a network blip, fall back to login.
        if (!cachedRaw && error?.code !== 'ERR_NETWORK' && error?.response?.status !== 401) {
          setIsAuthenticated(false);
          navigate('/', { replace: true });
        }
        // else: keep showing the cached UI; user can retry actions
      });
  }, [location.state, navigate]);

  if (isAuthenticated === null) {
    return <DndLoader label="Entering the realm..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// ==================== LAZY IMPORTS ====================
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const MarketingLanding = React.lazy(() => import('./pages/MarketingLanding'));
const PortalHub = React.lazy(() => import('./pages/PortalHub'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const CampaignPage = React.lazy(() => import('./pages/CampaignPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const AdminPage = React.lazy(() => import('./pages/AdminPage'));
const MyDecksPage = React.lazy(() => import('./pages/MyDecksPage'));
const PortalLauncher = React.lazy(() => import('./components/PortalLauncher'));

// ==================== LANDING WRAPPER ====================
// Public marketing page at "/". Logged-in users are bounced to the dashboard.
const LandingWrapper = () => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('session_token');
    if (token) {
      axios.get(`${API}/auth/me`)
        .then((response) => {
          try { localStorage.setItem('cached_user', JSON.stringify(response.data)); }
          catch (e) { console.warn('[LandingWrapper] cache user failed', e); }
          navigate('/hub', { replace: true });
        })
        .catch((error) => {
          if (error?.response?.status !== 401) {
            console.warn('[LandingWrapper] /auth/me failed (non-401):', error?.code || error?.message);
          }
          setChecking(false);
        });
    } else {
      setChecking(false);
    }
  }, [navigate]);

  if (checking) {
    return <DndLoader label="Loading..." />;
  }

  return <MarketingLanding />;
};

// ==================== AUTH WRAPPER ====================
// Login / register form at "/login" and "/register".
const AuthWrapper = ({ mode = 'login' }) => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('session_token');
    if (token) {
      axios.get(`${API}/auth/me`)
        .then((response) => {
          try { localStorage.setItem('cached_user', JSON.stringify(response.data)); }
          catch (e) { console.warn('[AuthWrapper] cache user failed', e); }
          navigate('/hub', { replace: true });
        })
        .catch((error) => {
          if (error?.response?.status !== 401) {
            console.warn('[AuthWrapper] /auth/me failed (non-401):', error?.code || error?.message);
          }
          setChecking(false);
        });
    } else {
      setChecking(false);
    }
  }, [navigate]);

  const handleLogin = (token) => {
    axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(response => {
        try { localStorage.setItem('cached_user', JSON.stringify(response.data)); }
        catch (e) { console.warn('[AuthWrapper] cache user failed', e); }
        navigate('/hub', { state: { user: response.data }, replace: true });
      })
      .catch((error) => {
        console.warn('[AuthWrapper] /auth/me after login failed:', error?.code || error?.message);
        localStorage.removeItem('session_token');
        toast.error('Failed to load user data');
      });
  };

  if (checking) {
    return <DndLoader label="Loading..." />;
  }

  return <LandingPage onLogin={handleLogin} initialMode={mode} />;
};

// ==================== APP ROUTER ====================
function AppRouter() {
  return (
    <React.Suspense fallback={<DndLoader label="Loading..." />}>
      <Routes>
        <Route path="/" element={<LandingWrapper />} />
        <Route path="/login" element={<AuthWrapper mode="login" />} />
        <Route path="/register" element={<AuthWrapper mode="signup" />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/hub" element={
          <ProtectedRoute>
            <PortalHub />
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/campaign/:campaignId" element={
          <ProtectedRoute>
            <CampaignPage />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        } />
        <Route path="/my-decks" element={
          <ProtectedRoute>
            <MyDecksPage />
          </ProtectedRoute>
        } />
      </Routes>
    </React.Suspense>
  );
}

// ==================== MAIN APP ====================
function App() {
  return (
    <div className="App min-h-screen bg-neutral-950">
      <Toaster 
        position="top-right" 
        theme="dark"
        expand={false}
        richColors
        closeButton
        duration={3000}
        visibleToasts={5}
        toastOptions={{
          style: {
            background: '#171717',
            border: '1px solid #262626',
            color: '#fafafa',
          },
        }}
      />
      <BrowserRouter>
        <AppRouter />
        {/*
          PortalLauncher is mounted at the top level (OUTSIDE <Routes>) so its
          iframe survives navigations between protected pages. If we mounted it
          inside ProtectedRoute, React Router would remount it on every nav and
          we'd lose the cached portal session every page change.
          The component self-gates: it renders nothing on `/auth/*` routes or
          when there's no `session_token` in localStorage.
        */}
        <React.Suspense fallback={null}>
          <PortalLauncher />
        </React.Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
